// src/hooks/useEmailVerificationPoll.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { auth, EMAIL_VERIFICATION_ACS } from "../firebase";
import {
    getIdTokenResult,
    reload,
    sendEmailVerification,
    onIdTokenChanged,
    type User,
} from "firebase/auth";

type PollState = {
    isVerified: boolean;
    isPolling: boolean;
    error?: string;
};

export function useEmailVerificationPoll() {
    const [state, setState] = useState<PollState>({
        isVerified: !!auth.currentUser?.emailVerified,
        isPolling: false,
    });

    const intervalRef = useRef<number | null>(null);
    const timeoutRef = useRef<number | null>(null);
    const mountedRef = useRef<boolean>(false);
    const startedRef = useRef<boolean>(false);

    const safeSetState = useCallback((updater: (s: PollState) => PollState) => {
        if (!mountedRef.current) return;
        setState(updater);
    }, []);

    const clearTimers = useCallback(() => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const stopInternal = useCallback(() => {
        clearTimers();
        startedRef.current = false; // 🔧 VIKTIGT: lås upp så vi kan starta igen
        safeSetState((s) => (s.isPolling ? { ...s, isPolling: false } : s));
    }, [clearTimers, safeSetState]);

    const markVerified = useCallback(async () => {
        try {
            localStorage.setItem("gw-email-verified", "true");
            try {
                new BroadcastChannel("gw-auth").postMessage({ type: "email-verified" });
            } catch { /* Safari/iframes kan kasta */ }
        } catch { /*OK*/}
        safeSetState((s) => ({ ...s, isVerified: true, error: undefined }));
        stopInternal();
    }, [safeSetState, stopInternal]);

    const checkNow = useCallback(async () => {
        const u: User | null = auth.currentUser;
        if (!u) {
            safeSetState((s) => ({ ...s, error: "Inte inloggad." }));
            return false;
        }
        try {
            await reload(u);                    // uppdatera user-objektet
            const ok = !!u.emailVerified;
            if (ok) {
                await getIdTokenResult(u, true);  // forcerad token-refresh
                await markVerified();             // signalera + stoppa
                return true;
            }
            // inte verifierad ännu
            safeSetState((s) => (s.isVerified ? { ...s, isVerified: false } : s));
            return false;
        } catch (e: any) {
            const msg = e?.message ?? String(e);
            safeSetState((s) => ({ ...s, error: msg }));
            return false;
        }
    }, [markVerified, safeSetState]);

    const startAutoPoll = useCallback(
        async (durationMs = 120000, intervalMs = 4000) => { // ⏱️ längre default
            if (startedRef.current) return;
            startedRef.current = true;

            clearTimers();
            safeSetState((s) => (s.isPolling ? s : { ...s, isPolling: true }));

            // Direkt första koll
            const firstOk = await checkNow();
            if (firstOk) return;

            // Intervall
            intervalRef.current = window.setInterval(() => {
                void checkNow();
            }, intervalMs) as unknown as number;

            // Dead-man timeout
            timeoutRef.current = window.setTimeout(() => {
                stopInternal(); // 🔧 återställer startedRef
            }, durationMs) as unknown as number;
        },
        [checkNow, clearTimers, safeSetState, stopInternal]
    );

    const stopPoll = useCallback(() => {
        stopInternal();
    }, [stopInternal]);

    const resendEmail = useCallback(async () => {
        const u = auth.currentUser;
        if (!u) {
            safeSetState((s) => ({ ...s, error: "Inte inloggad." }));
            return;
        }
        try {
            await sendEmailVerification(u, EMAIL_VERIFICATION_ACS);
            safeSetState((s) => ({ ...s, error: undefined }));
        } catch (e: any) {
            const msg = e?.code ? `${e.code}: ${e.message}` : e?.message ?? String(e);
            safeSetState((s) => ({ ...s, error: msg }));
        }
    }, [safeSetState]);

    // React på token-förändringar (snabbt när Googles sida väl slagit igenom)
    useEffect(() => {
        const unsub = onIdTokenChanged(auth, (u) => {
            if (u?.emailVerified) void markVerified();
        });
        return () => unsub();
    }, [markVerified]);

    // Kör en extra koll när fliken blir aktiv igen
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "visible") void checkNow();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [checkNow]);

    // Cross-tab signaler
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === "gw-email-verified" && e.newValue === "true") {
                void markVerified();
            }
        };
        window.addEventListener("storage", onStorage);

        let ch: BroadcastChannel | null = null;
        try {
            ch = new BroadcastChannel("gw-auth");
            ch.addEventListener("message", (ev: MessageEvent) => {
                if (ev.data?.type === "email-verified") void markVerified();
            });
        } catch { /* ignore */ }

        return () => {
            window.removeEventListener("storage", onStorage);
            if (ch) ch.close();
        };
    }, [markVerified]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            stopInternal(); // städa allt och lås upp
        };
    }, [stopInternal]);

    return {
        ...state,
        checkNow,
        startAutoPoll,
        stopPoll,
        resendEmail,
    };
}
